/**
 * React bindings of the services the Song & Timing panels read (W2-08 timing). Every hook subscribes to its
 * source, so panels follow edits from anywhere (undo, the inspector, another panel), locale and theme switches,
 * transport changes and project swaps. Text goes through `services.i18n`.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { AppServices } from '../../app/registry/types';
import type { UserSettings } from '../../app/settings/types';
import type { MusicInfo, TransportState } from '../../audio/types';
import type { Locale, MessageRef, Unsubscribe } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import { useExternalSelector } from '../../state/react';
import type { DocStore, EditorStore } from '../../state/types';
import { timingView, TIMING_TOPICS, type TimingView } from './timingDoc';

export interface TimingText {
  readonly locale: Locale;
  readonly t: (key: MessageKey | string, params?: Readonly<Record<string, string | number>>) => string;
  readonly tr: (ref: MessageRef) => string;
}

/** Translation through `services.i18n` (a new object per locale). */
export function useText(services: Pick<AppServices, 'i18n'>): TimingText {
  const { i18n } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => i18n.subscribe(cb), [i18n]);
  const get = useCallback((): Locale => i18n.locale, [i18n]);
  const locale = useSyncExternalStore(subscribe, get, get);
  return useMemo(() => ({ locale, t: (key, params) => i18n.t(key, params), tr: (r) => i18n.tr(r) }), [i18n, locale]);
}

export function useSettingsValue(services: Pick<AppServices, 'settings'>): UserSettings {
  const { settings } = services;
  const subscribe = useCallback((cb: () => void): Unsubscribe => settings.subscribe(cb), [settings]);
  const get = useCallback((): UserSettings => settings.value, [settings]);
  return useSyncExternalStore(subscribe, get, get);
}

/** A slice of the editor store (`services.editor`). */
export function useEditorSelect<T>(
  services: Pick<AppServices, 'editor'>,
  select: (s: EditorStore) => T,
  equal?: (a: T, b: T) => boolean,
): T {
  const { editor } = services;
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      editor.subscribe(() => {
        cb();
      }),
    [editor],
  );
  const get = useCallback((): EditorStore => editor.getState(), [editor]);
  return useExternalSelector(editor, subscribe, get, select, equal);
}

/** The transport state (playing, rate, mode). */
export function useTransport(services: Pick<AppServices, 'audio'>): TransportState {
  const transport = services.audio.transport;
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      transport.onChange(() => {
        cb();
      }),
    [transport],
  );
  const get = useCallback((): TransportState => transport.state, [transport]);
  return useSyncExternalStore(subscribe, get, get);
}

/** The decoded song of the audio engine (changes emit a transport change). */
export function useMusic(services: Pick<AppServices, 'audio'>): MusicInfo | null {
  const { audio } = services;
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      audio.transport.onChange(() => {
        cb();
      }),
    [audio],
  );
  const get = useCallback((): MusicInfo | null => audio.music, [audio]);
  return useSyncExternalStore(subscribe, get, get);
}

/** The open project's store (re-renders when services swap it). */
export function useStore(services: Pick<AppServices, 'store' | 'onChange'>): DocStore | null {
  const [, setVersion] = useState(0);
  useEffect(
    () =>
      services.onChange(() => {
        setVersion((v) => v + 1);
      }),
    [services],
  );
  return services.store;
}

const NO_STORE_SUBSCRIBE = (): Unsubscribe => () => undefined;

/** The timing view of a store (null without a project); re-renders when a timing record changes. */
export function useTimingView(store: DocStore | null): TimingView | null {
  // Document swaps (`load`) must re-run the selector even if the version counters did not move.
  const swaps = useRef(0);
  const subscribe = useCallback(
    (cb: () => void): Unsubscribe =>
      store === null
        ? NO_STORE_SUBSCRIBE()
        : store.subscribe((cs) => {
            if (cs.source === 'load') swaps.current++;
            cb();
          }),
    [store],
  );
  const stamp = useCallback((): number => {
    if (store === null) return 0;
    let n = swaps.current * 1e9;
    for (const t of TIMING_TOPICS) n += store.version(t);
    return n;
  }, [store]);
  const select = useCallback(
    (): TimingView | null => (store === null ? null : timingView(store.doc, store.idx)),
    [store],
  );
  return useExternalSelector(store, subscribe, stamp, select);
}
